/**
 * Core timeframe utilities for CEX SDK.
 * Provides interval conversion and resolution mapping utilities.
 */

// ============================================================================
// TIMEFRAME CONFIGURATION INTERFACE
// ============================================================================

/**
 * Timeframe configuration for each supported exchange.
 * Defines which intervals are natively supported vs. require synthetic expansion.
 */
export interface TimeframeConfig {
  /** Native intervals supported by the exchange's API */
  native: string[];
  /** Intervals that require synthetic expansion from larger timeframes */
  synthetic: string[];
  /** Minimum supported interval in seconds */
  minIntervalSeconds: number;
}

/**
 * Maps TradingView resolution strings to exchange-specific interval formats.
 */
export type IntervalMapper = (resolution: string) => string;

// ============================================================================
// CORE INTERVAL CONVERSION UTILITIES
// ============================================================================

/**
 * Converts interval string to seconds for calculations.
 */
export const intervalToSeconds = (interval: string): number => {
  // Handle Bybit formats: pure numbers (minutes) or single letters (D, W, M)
  if (/^\d+$/.test(interval)) {
    return parseInt(interval, 10) * 60;
  }
  // D/W are case-insensitive (both cases mean Day/Week)
  // M is case-SENSITIVE: uppercase 'M' = Month, lowercase 'm' = Minute (different units!)
  if (interval === 'D' || interval === 'd') return 86400;
  if (interval === 'W' || interval === 'w') return 604800;
  if (interval === 'M') return 2592000;

  // Handle LBank format: "minute1", "minute5", "hour1", "day1", etc. (letters first, then number)
  const lbankMatch = interval.match(/^(minute|hour|day|week|month)(\d+)$/i);
  if (lbankMatch) {
    const unit = lbankMatch[1].toLowerCase();
    const value = parseInt(lbankMatch[2], 10);
    switch (unit) {
      case 'minute':
        return value * 60;
      case 'hour':
        return value * 3600;
      case 'day':
        return value * 86400;
      case 'week':
        return value * 604800;
      case 'month':
        return value * 2592000;
      default:
        return 60;
    }
  }

  // Handle Coinstore WebSocket format: "min_1", "min_5", "hour_1", "day_1", "week_1" (unit_number)
  // NOTE: Month (mon_1) is NOT supported by Coinstore API
  const coinstoreMatch = interval.match(/^(min|hour|day|week)_(\d+)$/i);
  if (coinstoreMatch) {
    const unit = coinstoreMatch[1].toLowerCase();
    const value = parseInt(coinstoreMatch[2], 10);
    switch (unit) {
      case 'min':
        return value * 60;
      case 'hour':
        return value * 3600;
      case 'day':
        return value * 86400;
      case 'week':
        return value * 604800;
      default:
        return 60;
    }
  }

  const match = interval.match(/^(\d+)([a-zA-Z]+)$/);
  if (!match) return 60;

  const value = parseInt(match[1], 10);
  const unit = match[2];

  // Handle uppercase 'M' for month (TradingView convention) BEFORE toLowerCase
  if (unit === 'M') {
    return value * 2592000; // 30 days
  }

  const unitLower = unit.toLowerCase();

  switch (unitLower) {
    case 's':
    case 'sec':
      return value;
    case 'm':
    case 'min':
      return value * 60;
    case 'h':
    case 'hour':
      return value * 3600;
    case 'd':
    case 'day':
      return value * 86400;
    case 'w':
    case 'week':
      return value * 604800;
    case 'mo':
    case 'mon':
    case 'month':
      return value * 2592000; // 30 days
    case 'year':
      return value * 31536000; // 365 days
    default:
      return 60;
  }
};

/**
 * Converts TradingView resolution to seconds.
 */
export const resolutionToSeconds = (resolution: string): number => {
  const upper = resolution.toUpperCase();

  // Handle special cases
  if (upper === 'D' || upper === '1D') return 86400;
  if (upper === 'W' || upper === '1W') return 604800;
  if (upper === 'M' || upper === '1M') return 2592000;

  // Handle 'S' suffix (seconds)
  if (upper.endsWith('S')) {
    const value = parseInt(upper.replace('S', ''), 10);
    return isNaN(value) ? 60 : value;
  }

  // Handle 'H' suffix (hours)
  if (upper.endsWith('H')) {
    const value = parseInt(upper.replace('H', ''), 10);
    return isNaN(value) ? 3600 : value * 3600;
  }

  // Handle 'D' suffix (days)
  if (upper.endsWith('D')) {
    const value = parseInt(upper.replace('D', ''), 10);
    return isNaN(value) ? 86400 : value * 86400;
  }

  // Handle 'W' suffix (weeks)
  if (upper.endsWith('W')) {
    const value = parseInt(upper.replace('W', ''), 10);
    return isNaN(value) ? 604800 : value * 604800;
  }

  // Default: treat as minutes
  const minutes = parseInt(resolution, 10);
  return isNaN(minutes) ? 60 : minutes * 60;
};

// ============================================================================
// EXCHANGE TIMEFRAME REGISTRY
// ============================================================================

/**
 * Registry of exchange timeframe configurations.
 * Each exchange module registers its config via registerExchangeTimeframes().
 */
const exchangeTimeframesRegistry = new Map<string, TimeframeConfig>();

/**
 * Registers timeframe configuration for an exchange.
 * Called by each exchange module to register its config.
 */
export const registerExchangeTimeframes = (exchangeId: string, config: TimeframeConfig): void => {
  exchangeTimeframesRegistry.set(exchangeId, config);
};

/**
 * Gets the timeframe configuration for an exchange.
 */
export const getExchangeTimeframes = (exchangeId: string): TimeframeConfig | undefined => {
  return exchangeTimeframesRegistry.get(exchangeId);
};

// ============================================================================
// RESOLUTION UTILITIES
// ============================================================================

/**
 * Checks if an exchange natively supports a given resolution.
 */
export const isNativeResolution = (exchange: string, resolution: string): boolean => {
  const config = exchangeTimeframesRegistry.get(exchange);
  if (!config) return false;

  const seconds = resolutionToSeconds(resolution);

  // Check if any native interval matches
  return config.native.some(interval => intervalToSeconds(interval) === seconds);
};

/**
 * Checks if resolution requires ticker-based real-time updates.
 */
export const requiresTickerUpdates = (exchange: string, resolution: string): boolean => {
  const config = exchangeTimeframesRegistry.get(exchange);
  if (!config) return false;

  const seconds = resolutionToSeconds(resolution);
  return seconds < config.minIntervalSeconds;
};

/**
 * Gets all TradingView-compatible resolutions for an exchange.
 */
export const getExchangeResolutions = (exchange: string): string[] => {
  const config = exchangeTimeframesRegistry.get(exchange);
  if (!config) return ['1', '5', '15', '60', '240', '1D', '1W'];

  const resolutions = new Set<string>();

  // Add native intervals converted to TradingView format
  config.native.forEach(interval => {
    const seconds = intervalToSeconds(interval);
    if (seconds === 1) resolutions.add('1S');
    else if (seconds === 10) resolutions.add('10S');
    else if (seconds === 30) resolutions.add('30S');
    else if (seconds < 60)
      resolutions.add(`${seconds}S`); // Sub-minute seconds
    else if (seconds < 3600)
      resolutions.add(String(seconds / 60)); // Minutes
    else if (seconds < 86400)
      resolutions.add(String(seconds / 60)); // Hours in minutes
    else if (seconds === 86400) resolutions.add('1D');
    else if (seconds === 259200)
      resolutions.add('3D'); // 3 days
    else if (seconds === 604800) resolutions.add('1W');
    else if (seconds === 2592000)
      resolutions.add('1M'); // 30 days = 1 month
    else if (seconds === 31536000)
      resolutions.add('12M'); // 365 days = 1 year
    else if (seconds > 86400 && seconds < 604800) {
      // Multi-day intervals (e.g., 7d from Gate)
      resolutions.add(`${Math.floor(seconds / 86400)}D`);
    } else {
      // Unknown, add as minutes fallback
      resolutions.add(String(seconds / 60));
    }
  });

  // Add synthetic intervals
  config.synthetic.forEach(interval => {
    const seconds = intervalToSeconds(interval);
    if (seconds === 1) resolutions.add('1S');
    else if (seconds === 10) resolutions.add('10S');
  });

  return Array.from(resolutions).sort((a, b) => {
    const aSeconds = resolutionToSeconds(a);
    const bSeconds = resolutionToSeconds(b);
    return aSeconds - bSeconds;
  });
};
